import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";

/**
 * Turns whatever is still outstanding on a demand sheet into a primary sales
 * order. The order is posted to the existing /order endpoint carrying the
 * demand_id, which is what links the two together.
 */
const GenerateOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [res, resInvoiceNo] = await Promise.all([
          Http.get(`/demand-generate-order/${id}`),
          Http.get("/last-code-increment-by-one/orders/receipt_no/PS"),
        ]);

        const demand = res.data.demand;
        const now = new Date();

        setInputs({
          demand_id: demand.id,
          invoice_no: resInvoiceNo.data,
          date: now.toISOString().slice(0, 10),
          order_date: demand.order_date,
          time: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
          distributor_id: demand.distributor_id,
          distributor_name: demand.distributor ? demand.distributor.name : "",
          sr_id: demand.sr_id,
          sr_name: demand.sr ? demand.sr.name : "",
          payment_type: demand.payment_type,
          payment_date: demand.payment_date,
          received_amount: demand.payment_amount,
          previous_due: res.data.previous_due,
          notes: demand.notes || "",
        });

        // Only the outstanding part is offered, capped by what is in stock.
        setList(
          res.data.items
            .filter((item) => item.remaining_qty > 0)
            .map((item) => ({
              ...item,
              inv_qty: item.remaining_qty <= item.stock ? item.remaining_qty : 0,
              to_qty: 0,
              line_total: (item.remaining_qty <= item.stock ? item.remaining_qty : 0) * item.price,
            }))
        );
      } catch (e) {
        setBlocked(e.response?.data?.message || "This demand cannot be turned into an order.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const totalOf = (rows) => rows.reduce((acc, row) => acc + Number(row.line_total || 0), 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleItemChange = (e, index) => {
    const { name, value } = e.target;
    const rows = [...list];

    if (name === "inv_qty") {
      if (Number(value) > Number(rows[index].remaining_qty)) {
        Swal.fire("Not allowed!", `The allowed quantity is ${rows[index].remaining_qty}.`, "warning");
        rows[index].inv_qty = rows[index].remaining_qty;
      } else if (Number(value) > Number(rows[index].stock)) {
        Swal.fire("Not allowed!", `Only ${rows[index].stock} left in stock.`, "warning");
        rows[index].inv_qty = rows[index].stock;
      } else if (Number(value) < 0) {
        rows[index].inv_qty = 0;
      } else {
        rows[index].inv_qty = value;
      }
    } else {
      rows[index][name] = value;
    }

    rows[index].line_total = Number(rows[index].inv_qty || 0) * Number(rows[index].price || 0);
    setList(rows);
    setInputs((values) => ({ ...values, total_price: totalOf(rows) }));
  };

  const formSubmit = () => {
    const rows = list.filter((row) => Number(row.inv_qty) > 0);

    if (rows.length < 1) {
      Swal.fire("Warning!", "Nothing to invoice: set a quantity on at least one item.", "warning");
      return;
    }

    const total = totalOf(rows);

    const payload = {
      demand_id: Number(id),
      invoice_no: inputs.invoice_no,
      date: inputs.date,
      time: inputs.time,
      distributor_id: inputs.distributor_id,
      sr_id: Number(inputs.sr_id),
      payment_type: inputs.payment_type,
      total_price: total,
      received_amount: inputs.received_amount || 0,
      due_amount: total - Number(inputs.received_amount || 0),
      notes: inputs.notes,
      items: rows.map((row) => ({
        id: row.id,
        unit_id: row.unit_id,
        pack_size: row.pack_size,
        order_qty: row.remaining_qty,
        inv_qty: row.inv_qty,
        to_qty: row.to_qty || 0,
        price: row.price,
      })),
    };

    Http.post("/order", payload)
      .then((res) => {
        navigate("/primary-sales", { state: { message: res.data.message, type: "success" } });
      })
      .catch((e) => {
        setError(e.response?.data?.errors || {});
        if (e.response?.data?.message && !e.response?.data?.errors) {
          Swal.fire("Failed!", e.response.data.message, "error");
        }
      });
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small">{error[fieldName]}</div>;
    }
    return null;
  };

  if (blocked) {
    return (
      <div className="am-mainpanel">
        <IsLogin></IsLogin>
        <div className="am-pagebody">
          <div className="alert alert-warning">{blocked}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Generate Order from Demand</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            {loading ? (
              <ProgressSpinner />
            ) : (
              <>
                <div className="row">
                  <div className="col-lg">
                    <label>
                      Invoice No <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="invoice_no"
                      value={inputs.invoice_no || ""}
                      onChange={handleChange}
                    />
                    {displayError("invoice_no")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Invoice Date <i className="fa fa-star required"></i>
                    </label>
                    <input type="date" className="form-control" name="date" value={inputs.date || ""} onChange={handleChange} />
                    {displayError("date")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Time <i className="fa fa-star required"></i>
                    </label>
                    <input type="time" className="form-control" name="time" value={inputs.time || ""} onChange={handleChange} />
                    {displayError("time")}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Distributor</label>
                    <input type="text" className="form-control" value={inputs.distributor_name || ""} disabled />
                    {displayError("distributor_id")}
                  </div>
                  <div className="col-lg">
                    <label>Sales Person</label>
                    <input type="text" className="form-control" value={inputs.sr_name || ""} disabled />
                    {displayError("sr_id")}
                  </div>
                  <div className="col-lg">
                    <label>Payment Type</label>
                    <input type="text" className="form-control" value={inputs.payment_type || ""} disabled />
                    {displayError("payment_type")}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Notes</label>
                    <textarea className="form-control" name="notes" value={inputs.notes || ""} onChange={handleChange} />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="card-solid w-100">
                    <div className="card-header with-border text-center">
                      <h5 className="card-title mb-0">Outstanding Items in the Demand</h5>
                    </div>
                    <div className="card-body p-0 table-responsive">
                      <table className="table table-sm table-bordered table-striped mb-0">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Product</th>
                            <th>Crtn Size</th>
                            <th>Demand Qty</th>
                            <th>Delivered</th>
                            <th>Remaining</th>
                            <th>Stock</th>
                            <th>Inv. Qty</th>
                            <th>T/O Qty</th>
                            <th>Rate</th>
                            <th>Line Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.length < 1 && (
                            <tr>
                              <td colSpan={11}>
                                <p style={{ color: "red" }} className="text-center mt-3">
                                  Nothing outstanding on this demand.
                                </p>
                              </td>
                            </tr>
                          )}

                          {list.map((x, i) => (
                            <tr key={x.id}>
                              <td>{x.code}</td>
                              <td>{x.name}</td>
                              <td>{x.pack_size}</td>
                              <td>{x.demand_qty}</td>
                              <td>{x.delivered_qty}</td>
                              <td>{x.remaining_qty}</td>
                              <td>
                                {x.stock}
                                {x.remaining_qty > x.stock ? (
                                  <span className="text-danger d-block">
                                    {Number(x.stock) > 0 ? `Only ${x.stock} available` : "Out of stock"}
                                  </span>
                                ) : null}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="inv_qty"
                                  value={x.inv_qty}
                                  max={x.remaining_qty}
                                  onChange={(e) => handleItemChange(e, i)}
                                />
                                {displayError("items." + i + ".inv_qty")}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="to_qty"
                                  value={x.to_qty}
                                  onChange={(e) => handleItemChange(e, i)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="price"
                                  value={x.price}
                                  onChange={(e) => handleItemChange(e, i)}
                                />
                                {displayError("items." + i + ".price")}
                              </td>
                              <td>
                                <input type="number" className="form-control" value={x.line_total || 0} disabled />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={10} className="text-right" style={{ lineHeight: "40px" }}>
                              Total Price
                            </td>
                            <td>
                              <input type="text" className="form-control" value={inputs.total_price || 0} disabled />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={10} className="text-right" style={{ lineHeight: "40px" }}>
                              Previous Due
                            </td>
                            <td>
                              <input type="text" className="form-control" value={inputs.previous_due || 0} disabled />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={10} className="text-right" style={{ lineHeight: "40px" }}>
                              Received Amount
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                name="received_amount"
                                value={inputs.received_amount || ""}
                                onChange={handleChange}
                              />
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-3">
                    <button className="btn btn-primary btn-block mg-b-10" onClick={formSubmit}>
                      <i className="fa fa-send mg-r-10"></i> Process Order
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateOrder;
